import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, BookOpen, Bitcoin, Clock, Users, Check, ExternalLink, Zap, Shield, Target } from 'lucide-react';
import { Button } from './ui/button';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

const CoursesSection = () => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  const courses = [
    {
      id: 'sovereignty',
      title: '7 Days to Sovereignty',
      subtitle: 'Your Unshackled Living Journey',
      price: '$10',
      priceNote: 'One-time • Lifetime access',
      description: 'A transformative week-long journey to help you take the first steps toward financial, physical, and mental sovereignty. Perfect for those ready to break free from system dependencies.',
      icon: Shield,
      color: 'amber',
      features: [
        '7 days of structured sovereignty content',
        'Daily action items and exercises',
        'Personal journaling with guided prompts',
        'Expert resources from thought leaders',
        'Case studies and real-world examples',
        'Lifetime access to all materials'
      ],
      cta: 'Start Your Journey',
      ctaAction: () => user ? navigate('/course/checkout') : navigate('/auth'),
      badge: 'Most Popular',
      isInternal: true
    },
    {
      id: 'bitcoin',
      title: 'Bitcoin Fundamentals',
      subtitle: 'Master Sound Money',
      price: '$499',
      priceNote: 'Live cohort course',
      description: 'A comprehensive deep-dive into Bitcoin—from the philosophical foundations of sound money to practical self-custody and security. Learn why Bitcoin matters and how to use it confidently.',
      icon: Bitcoin,
      color: 'orange',
      features: [
        'Live cohort-based learning experience',
        'Direct access to instructor Q&A',
        'Complete Bitcoin education curriculum',
        'Self-custody and security training',
        'Community of like-minded learners',
        'Certificate of completion'
      ],
      cta: 'Learn More on Maven',
      ctaAction: () => window.open('https://maven.com/the-web3-prof/crypto-fundamentals', '_blank'),
      badge: 'Live Course',
      isInternal: false
    }
  ];

  return (
    <section id="courses" className={`py-24 ${isDark ? 'bg-stone-900/30' : 'bg-stone-100/50'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 mb-6">
            <BookOpen className="w-4 h-4 text-amber-500" />
            <span className="text-amber-500 font-medium text-sm">COURSES</span>
          </div>
          
          <h2 className={`text-4xl sm:text-5xl font-bold mb-6 ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
            Level Up Your Sovereignty
          </h2>
          
          <p className={`text-lg leading-relaxed ${isDark ? 'text-stone-400' : 'text-stone-600'}`}>
            Whether you're just starting your journey or ready to master Bitcoin, 
            we have a course designed to help you reclaim control of your life.
          </p>
        </div>

        {/* Course Cards */}
        <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {courses.map((course) => {
            const Icon = course.icon;
            const colorClasses = course.color === 'amber' 
              ? {
                  badge: isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700',
                  icon: isDark ? 'bg-amber-500/10 text-amber-500' : 'bg-amber-100 text-amber-600',
                  button: 'bg-amber-500 hover:bg-amber-600 text-stone-950',
                  check: isDark ? 'text-amber-500' : 'text-amber-600'
                }
              : {
                  badge: isDark ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-100 text-orange-700',
                  icon: isDark ? 'bg-orange-500/10 text-orange-500' : 'bg-orange-100 text-orange-600',
                  button: isDark 
                    ? 'bg-stone-800 hover:bg-stone-700 text-stone-100 border border-stone-700' 
                    : 'bg-white hover:bg-stone-50 text-stone-800 border border-stone-300',
                  check: isDark ? 'text-orange-500' : 'text-orange-600'
                };

            return (
              <div 
                key={course.id}
                className={`relative rounded-3xl border overflow-hidden transition-all hover:shadow-xl ${
                  isDark 
                    ? 'bg-stone-900/80 border-stone-800 hover:border-stone-700' 
                    : 'bg-white border-stone-200 hover:border-stone-300 shadow-lg'
                }`}
              >
                {/* Badge */}
                <div className="absolute top-6 right-6">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${colorClasses.badge}`}>
                    {course.badge}
                  </span>
                </div>

                <div className="p-8">
                  {/* Icon & Title */}
                  <div className="flex items-start gap-4 mb-6">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${colorClasses.icon}`}>
                      <Icon className="w-7 h-7" />
                    </div>
                    <div>
                      <h3 className={`text-2xl font-bold ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
                        {course.title}
                      </h3>
                      <p className={`text-sm ${isDark ? 'text-stone-500' : 'text-stone-500'}`}>
                        {course.subtitle}
                      </p>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="mb-6">
                    <div className="flex items-baseline gap-2">
                      <span className={`text-4xl font-bold ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
                        {course.price}
                      </span>
                      <span className={`text-sm ${isDark ? 'text-stone-500' : 'text-stone-500'}`}>
                        {course.priceNote}
                      </span>
                    </div>
                  </div>

                  {/* Description */}
                  <p className={`mb-6 leading-relaxed ${isDark ? 'text-stone-400' : 'text-stone-600'}`}>
                    {course.description}
                  </p>

                  {/* Features */}
                  <div className="space-y-3 mb-8">
                    {course.features.map((feature, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${colorClasses.check}`} />
                        <span className={`text-sm ${isDark ? 'text-stone-300' : 'text-stone-600'}`}>
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  <Button
                    onClick={course.ctaAction}
                    className={`w-full h-12 font-semibold text-base ${colorClasses.button}`}
                    data-testid={`course-${course.id}-cta`}
                  >
                    {course.cta}
                    {course.isInternal ? (
                      <ArrowRight className="w-5 h-5 ml-2" />
                    ) : (
                      <ExternalLink className="w-4 h-4 ml-2" />
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className={`text-sm ${isDark ? 'text-stone-500' : 'text-stone-500'}`}>
            Not sure where to start? 
            <button 
              onClick={() => user ? navigate('/quiz') : navigate('/auth')}
              className="text-amber-500 hover:text-amber-400 font-medium ml-1"
            >
              Take the free Sovereignty Quiz
            </button>
            {' '}to discover your path.
          </p>
        </div>
      </div>
    </section>
  );
};

export default CoursesSection;
